import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useState, type JSX } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { some } from 'lodash';
import LivePanel from '../components/LivePanel/LivePanel';
import { Timestamp } from '../services/Timestamp';
import useQueryParams from '../hooks/useQueryParams';
import { approximateTimestamp, isNavigableTimestamp } from '../utils/timestamp-utils';
import { useTargetVesselTracks } from '../hooks/useTargetVesselTracks';
import { useTripsPanelStateManager } from '../hooks/useTripsPanelStateManager';
import { useTargetVessel } from '../hooks/useTargetVessel';
import useHoveredVesselTracks from '../hooks/useHoveredVesselTracks';
import { useManageTimestamps } from '../hooks/useManageTimestamps';
import { useTrackPageView } from '../hooks/useTrackPageView';
import { useMapData } from '../hooks/useMapData';
import { useModeSwitches } from '../hooks/useModeSwitches';
import Scrubber from '../components/Scrubber/Scrubber';
import NoDataMessage from '../components/NoDataMessage';
import TripsPanel from '../components/TripsPanel/TripsPanel';
import BlurMap from '../components/BlurMap';
import MapMode from '../components/Map/components/MapMode';
import { GlobalSearch } from '../components/GlobalSearch/GlobalSearch';
import RightSideControls from '../components/RightSideControls';
import TripsHistoryToggle from '../components/TripsHistoryToggle';
import { TripsPanelState, type TripJobPair, Mode } from '../types';
import { defaultFilters } from '../components/GlobalContextProvider/initial-trips-state';
import RateAppFlow from '../components/RateApp/RateAppFlow';
import { Event, Feature, Page, VesselTargetSource } from '../types';
import { useDispatch, useTripsContext } from '../components/GlobalContextProvider/GlobalContextProvider';
import { useAppInsights } from '../hooks/useAppInsights';
import { getEventFactory } from '../utils/get-event-factory';
import { useShallowNavigate } from '../hooks/useShallowNavigate';
import { useDetectMobileScreenWidth } from '../hooks/useDetectMobileScreenWidth';
import { useAppContext } from '../components/AppContextProvider/AppContextProvider';
import GlobalExceptionHandlerModal from '../components/GlobalExceptionHandler/GlobalExceptionHandlerModal';
import { ActionType } from '../components/GlobalContextProvider/global-reducer';
import { useClearPreviousOperationData } from '../hooks/useClearPreviousOperationData';
import ChartSettings from '../components/Map/components/ChartSettings/ChartSettings';
import { useOperation } from '../hooks/useOperation';
import { useTripFilters } from '../components/GlobalContextProvider/hooks/useTripFilters';

const App: FC = () => {
  const { timestamp } = useQueryParams();
  const { mode } = useAppContext();
  const context = useTripsContext();
  const { operations, tripsPanelState } = context;
  const dispatch = useDispatch();
  const shallowNavigate = useShallowNavigate();
  const appInsights = useAppInsights();
  const [jobStartTimestamp, setJobStartTimestamp] = useState<Timestamp>(null);
  const { operation, operationKey, setOperation } = useOperation();
  useClearPreviousOperationData(operationKey);
  const { vesselName: filtersVesselName } = useTripFilters();

  const { playbackTimestamp, currentTimestamp, handleSetPlaybackTimestamp } = useManageTimestamps(
    timestamp,
    mode,
    operation?.timezone
  );

  const isWorldView = useMemo(() => {
    if (!operations?.length) {
      return !operationKey;
    }
    return !some(operations, o => o.key === operationKey);
  }, [operationKey, operations]);

  const [tripJobPair, setTripJobPair] = useState<TripJobPair>(null);
  const { job: hoveredJob, trip } = tripJobPair || {};
  const hoveredJobVesselTracks = useHoveredVesselTracks(operationKey, trip?.tripId, hoveredJob?.jobId);
  const [isDraggingScrubber, setIsDraggingScrubber] = useState(false);
  const isMobileView = useDetectMobileScreenWidth();

  useTrackPageView(mode, isWorldView, operation, operationKey);

  const handleSetJobStartTimestamp = useCallback((timestamp: Timestamp): void => {
    const approximatedTimestamp = approximateTimestamp(timestamp);
    setJobStartTimestamp(approximatedTimestamp);
  }, []);

  const isTripsPanelOpened = useMemo((): boolean => {
    if (isWorldView) {
      return false;
    }

    return tripsPanelState ? tripsPanelState === TripsPanelState.Open : !isMobileView;
  }, [tripsPanelState, isMobileView, isWorldView]);

  const page = useMemo(() => {
    if (mode === Mode.Playback) {
      return Page.Playback;
    }

    return Page.Liveview;
  }, [mode]);

  const tripsPanelStateManager = useTripsPanelStateManager(page, isMobileView, operation?.name ?? operationKey);

  const { switchToLiveview, switchToPlayback } = useModeSwitches(
    tripsPanelState,
    tripsPanelStateManager,
    isMobileView,
    handleSetPlaybackTimestamp,
    setIsDraggingScrubber
  );
  const { liveData, vesselPositions, vesselData, isPlaybackDataLoading, isError, isLiveDataLoading } = useMapData(
    operationKey,
    mode,
    playbackTimestamp as Timestamp
  );

  const isLoading = useMemo(
    () => (mode === Mode.Liveview && !liveData) || !operations?.length,
    [liveData, mode, operations]
  );

  const isVesselDataLoading = useMemo(() => {
    if (mode === Mode.Liveview) {
      return isLiveDataLoading;
    }

    if (mode === Mode.Playback) {
      return isPlaybackDataLoading;
    }

    return false;
  }, [isPlaybackDataLoading, isLiveDataLoading, mode]);

  const { targetVessel, setTargetVesselMmsi, setIsLockedOnTarget } = useTargetVessel(vesselData);

  useEffect(() => {
    if (!targetVessel?.mmsi) {
      return;
    }

    if (filtersVesselName === targetVessel?.name) {
      return;
    }

    if (targetVessel?.name) {
      dispatch({ type: ActionType.UPDATE_VESSEL_NAME, payload: targetVessel?.name });
    }
  }, [targetVessel]);

  const isMapVisible = useMemo(() => {
    return !!operations?.length && (!operationKey || !isLoading);
  }, [isLoading, operationKey, operations]);

  const isScrubberVisible = useMemo(() => {
    return mode === Mode.Playback && operation?.timezone && !!playbackTimestamp;
  }, [mode, operation]);

  const isCollapsedTimelineVisible = useMemo(() => {
    return mode === Mode.Liveview && operation?.timezone && !!currentTimestamp;
  }, [mode, operation]);

  const targetVesselTracks = useTargetVesselTracks(
    targetVessel,
    playbackTimestamp as Timestamp,
    isDraggingScrubber,
    operationKey,
    liveData
  );

  const handleOperationSelect = useCallback(
    (value: string): void => {
      const { endDate, startDate } = defaultFilters;
      dispatch({ type: ActionType.UPDATE_START_DATE, payload: startDate });
      dispatch({ type: ActionType.UPDATE_END_DATE, payload: endDate });
      handleTargetVesselChange(null, null);
      setOperation(value);
      if (!value) {
        handleSetPlaybackTimestamp(null);
      }
      appInsights.trackAnalyticsEvent(getEventFactory(Event.OperationSelected)(page, value));
    },
    [targetVessel?.mmsi, playbackTimestamp]
  );

  const handleMapInit = (isError: boolean, page: Page): JSX.Element => {
    if (isError) {
      return <GlobalExceptionHandlerModal errorMessage="An error occurred on map init" page={page} />;
    }
    return <BlurMap operation={operation?.name ?? operationKey} />;
  };

  const handleTargetVesselChange = useCallback(
    (mmsi: string = null, name: string = null) => {
      handleTargetLock(mmsi);
      setTargetVesselMmsi(mmsi);
      if (mmsi) {
        dispatch({ type: ActionType.UPDATE_VESSEL_NAME, payload: name });
      } else {
        dispatch({ type: ActionType.UPDATE_VESSEL_NAME, payload: defaultFilters.vesselName });
      }
    },
    [targetVessel]
  );

  useLayoutEffect(() => {
    if (isDraggingScrubber) {
      return;
    }

    shallowNavigate({
      port: operationKey,
      mmsi: targetVessel?.mmsi,
      timestamp: isNavigableTimestamp(playbackTimestamp as Timestamp) ? playbackTimestamp : null,
    });
  }, [playbackTimestamp, targetVessel?.mmsi, isDraggingScrubber, operationKey]);

  useLayoutEffect(() => {
    if (timestamp && !jobStartTimestamp) {
      handleSetJobStartTimestamp(Timestamp.fromSeconds(Number(timestamp)));
    }

    if (!timestamp && mode !== Mode.Liveview) {
      switchToLiveview();
    }

    if (timestamp && mode !== Mode.Playback) {
      switchToPlayback();
    }
  }, [timestamp]);

  useEffect(() => {
    if (!targetVessel) {
      return;
    }

    const event = targetVessel.isLockedOnTarget
      ? getEventFactory(Event.VesselTargetted)(
          page,
          targetVessel.name,
          targetVessel.mmsi,
          targetVessel.type,
          window.location.href,
          currentTimestamp as Timestamp,
          VesselTargetSource.ClickOnRecenterButton
        )
      : getEventFactory(Event.VesselUntargeted)(
          page,
          targetVessel.name,
          targetVessel.mmsi,
          targetVessel.type,
          window.location.href,
          currentTimestamp as Timestamp
        );
    appInsights.trackAnalyticsEvent(event);
  }, [targetVessel]);

  const handleVesselSearchResultClick = (
    mmsi: number,
    operation: string,
    vesselName: string,
    timestamp: Timestamp
  ): void => {
    setTargetVesselMmsi(mmsi.toString());
    dispatch({ type: ActionType.UPDATE_VESSEL_NAME, payload: vesselName });
    setIsLockedOnTarget(true);
    if (timestamp) {
      handleSetPlaybackTimestamp(timestamp);
      if (mode === Mode.Liveview) {
        switchToPlayback();
      }
    } else if (mode === Mode.Playback) {
      switchToLiveview();
    }
    setOperation(operation);
  };

  const handleSelectJobOrTrip = (operation: string, mmsi: string, timestamp: Timestamp, vesselName: string): void => {
    setOperation(operation);
    handleTargetVesselChange(mmsi, vesselName);
    handleSetJobStartTimestamp(timestamp);
    handleSetPlaybackTimestamp(timestamp);
  };

  const handleTargetLock = (mmsi: string): void => {
    setIsLockedOnTarget(!!mmsi);
  };

  return (
    <Flex flexDirection="row">
      <RateAppFlow parent={Feature.Map} page={page} />
      {!!operation && (
        <TripsPanel
          isOpen={isTripsPanelOpened}
          onClose={tripsPanelStateManager.close}
          setStartTimestamp={handleSetJobStartTimestamp}
          setTimestamp={handleSetPlaybackTimestamp}
          page={page}
          onTargetVesselChange={handleTargetVesselChange}
          setHoveredJob={setTripJobPair}
          operation={operation}
          targetVessel={targetVessel}
          playbackTimestamp={playbackTimestamp as Timestamp}
        />
      )}
      <Box className="pb-page-background">
        <GlobalSearch
          onOperationSelected={handleOperationSelect}
          operation={operation}
          operationKey={operationKey}
          isWorldView={isWorldView}
          page={page}
          timestamp={currentTimestamp as Timestamp}
          targetVessel={targetVessel}
          timezone={operation?.timezone}
          isVesselDataLoading={isVesselDataLoading}
          isTripsPanelOpened={isTripsPanelOpened}
          onVesselPositionReceived={handleVesselSearchResultClick}
          onJobOrTripSelected={handleSelectJobOrTrip}
          onVesselSelected={handleTargetVesselChange}
        />
        <RightSideControls isTripsPanelOpened={isTripsPanelOpened}>
          {!isWorldView && (
            <>
              <TripsHistoryToggle toggleTripsPanel={tripsPanelStateManager.toggle} />
              {isMapVisible && (
                <ChartSettings
                  page={page}
                  operation={operation?.name ?? operationKey}
                  isTripsPanelOpen={isTripsPanelOpened}
                />
              )}
            </>
          )}
        </RightSideControls>
        <NoDataMessage isShown={!isWorldView && !(isLoading || isPlaybackDataLoading) && !vesselPositions?.length} />
        {isMapVisible ? (
          <MapMode
            vesselPositions={vesselPositions ?? []}
            vesselData={vesselData}
            setLockedOnTarget={setIsLockedOnTarget}
            timestamp={currentTimestamp as Timestamp}
            targetVessel={targetVessel}
            onTargetVesselChange={handleTargetVesselChange}
            operation={operation}
            targetVesselTracks={targetVesselTracks}
            page={page}
            operations={operations}
            onOperationSelect={handleOperationSelect}
            isWorldView={isWorldView}
            isTripsPanelOpen={isTripsPanelOpened}
            pageMode={mode}
            hoveredJob={hoveredJob}
            timezone={operation?.timezone}
            hoveredJobVesselTracks={hoveredJobVesselTracks}
          />
        ) : (
          handleMapInit(isError, page)
        )}

        {isScrubberVisible && (
          <Scrubber
            timestamp={playbackTimestamp as Timestamp}
            setTimestamp={handleSetPlaybackTimestamp}
            targetVessel={targetVessel}
            vesselPositions={vesselPositions}
            isTripsPanelOpen={isTripsPanelOpened}
            isDraggingScrubber={isDraggingScrubber}
            setIsDraggingScrubber={setIsDraggingScrubber}
            operation={operation}
            switchToLiveview={switchToLiveview}
            onTargetLock={handleTargetLock}
            jobStartTimestamp={jobStartTimestamp}
            setStartTimestamp={setJobStartTimestamp}
          />
        )}
        {isCollapsedTimelineVisible && (
          <LivePanel
            isTripsPanelOpen={isTripsPanelOpened}
            targetVessel={targetVessel}
            vesselPositions={vesselPositions}
            operation={operation}
            onTargetLock={handleTargetLock}
            setStartTimestamp={setJobStartTimestamp}
            setTimestamp={handleSetPlaybackTimestamp}
          />
        )}
      </Box>
    </Flex>
  );
};
export default App;
